import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assigneeFor,
  IssueResolution,
  IssueState,
  MAX_COST_CAP_OVERRIDE_MULTIPLIER,
  PullRequestState,
  RunStatus,
  TERMINAL_RUN_STATUSES,
} from '@aiai/shared';
import {
  cancelRun,
  closeIssue,
  deleteIssue,
  fetchIssue,
  fetchIssueRuns,
  fetchProject,
  fetchProjectIssuesBoard,
  fetchRun,
  mergeRunPullRequest,
  runIssue,
  updateIssue,
} from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { projectNav } from '../lib/projectNav';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { StatusBadge } from '../components/StatusBadge';
import { AssigneeChip } from '../components/AssigneeChip';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Callout } from '../components/Callout';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { OverflowMenu } from '../components/OverflowMenu';
import { ArrowRight, ChevronLeft, ChevronRight, GitMerge, XCircle, Trash2 } from 'lucide-react';
import { Modal } from '../components/Modal';
import { Markdown } from '../components/Markdown';
import { IssueTimeline } from '../components/IssueTimeline';
import { PmThinkingDots } from '../components/PmThinkingDots';
import { Spinner } from '../components/Spinner';
import { ErrorState, LoadingState } from '../components/States';
import { ChangesPanel } from '../components/ChangesPanel';
import { formatRelative } from '../lib/format';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { useCapabilities } from '../hooks/useCapabilities';
import { personLabel, useViewerEmail } from '../lib/people';
import { useIssueSocket } from '../hooks/useIssueSocket';
import { TagChips } from '../components/TagInput';

export function IssueDetailPage() {
  const { issueId = '' } = useParams();
  const navigate = useNavigate();
  const { wp } = useWorkspacePath();
  const viewerEmail = useViewerEmail();
  const queryClient = useQueryClient();

  const issueQuery = useQuery({
    queryKey: qk.issue(issueId),
    queryFn: () => fetchIssue(issueId),
    enabled: !!issueId,
  });

  // Canonicalize the URL to the pretty code form: if we arrived via the UUID
  // (or a lower-cased code) and the issue has a code, replace the address with
  // /issues/OMHB-42. Guarded on code + inequality so it never loops.
  const canonicalCode = issueQuery.data?.code ?? null;
  useEffect(() => {
    if (canonicalCode && issueId !== canonicalCode) {
      navigate(wp(`/issues/${canonicalCode}`), { replace: true });
    }
  }, [canonicalCode, issueId, navigate, wp]);

  const projectId = issueQuery.data?.projectId ?? '';
  // projectId (UUID) still keys the board-invalidation below (the board caches
  // on the resolved UUID); the project fetch itself goes code-first.
  const projectRef = issueQuery.data?.projectCode ?? projectId;

  // A project `contributor` grant (or org owner) may edit,
  // approve, close, and comment on this issue's board; a `viewer` grant sees
  // it read-only — PROJECT_ROLE_CAPABILITIES.viewer holds none of these.
  // Merge/cancel/trigger-run/hard-delete stay owner-only (no project role
  // grants them), so they aren't resource-scoped here.
  const { can, isWorkspaceOwner } = useCapabilities();
  const canTriggerRun = can('run:trigger');
  const canStopRun = can('run:stop');
  const canMergePr = can('pr:merge');
  const canDeleteIssue = can('issue:delete');
  const canEditIssue = can('issue:edit', { projectId });
  const canApproveIssue = can('issue:approve', { projectId });
  const canCloseIssue = can('issue:close', { projectId });
  const canCommentOnIssue = can('issue:comment', { projectId });

  const projectQuery = useQuery({
    queryKey: qk.project(projectRef),
    queryFn: () => fetchProject(projectRef),
    enabled: !!projectRef,
  });

  // Previous / next issue: neighbours in the board's default order (status
  // 'all'), first page only (the API caps pageSize at 100). An issue outside
  // that page gets disabled buttons.
  const neighboursQuery = useQuery({
    queryKey: qk.projectIssueNeighbours(projectId),
    queryFn: () =>
      fetchProjectIssuesBoard(projectId, { status: 'all', page: 0, pageSize: 100 }),
    enabled: !!projectId,
    staleTime: 30_000,
  });
  const neighbourItems = neighboursQuery.data?.items ?? [];
  const currentIndex = neighbourItems.findIndex((i) => i.id === issueQuery.data?.id);
  const prevIssue = currentIndex > 0 ? neighbourItems[currentIndex - 1] : undefined;
  const nextIssue =
    currentIndex >= 0 ? neighbourItems[currentIndex + 1] : undefined;

  // The Team dial: drives assignee chip for `open` and `proposed` issues,
  // and whether a proposed issue waits for a PM plan before the Approve bar shows.
  const autonomy = projectQuery.data?.autonomy;

  const runsQuery = useQuery({
    queryKey: qk.issueRuns(issueId),
    queryFn: () => fetchIssueRuns(issueId),
    enabled: !!issueId,
  });

  // Fetch the full run detail only once the latest run is terminal — terminal
  // runs are immutable so we never need to refetch.
  const latest = issueQuery.data?.latestRun ?? runsQuery.data?.[0];
  const isLatestTerminal =
    !!latest && TERMINAL_RUN_STATUSES.includes(latest.status);

  const latestRunDetailQuery = useQuery({
    queryKey: qk.run(latest?.id ?? ''),
    queryFn: () => fetchRun(latest!.id),
    enabled: isLatestTerminal,
    staleTime: Infinity,
  });

  useDocumentTitle(
    issueQuery.data
      ? issueQuery.data.code
        ? `${issueQuery.data.code} ${issueQuery.data.title}`
        : issueQuery.data.title
      : undefined,
  );

  const runMutation = useMutation({
    mutationFn: (costCapUsdOverride?: number) =>
      costCapUsdOverride !== undefined
        ? runIssue(issueId, { costCapUsdOverride })
        : runIssue(issueId),
    onSuccess: (run) => {
      queryClient.invalidateQueries({ queryKey: qk.issue(issueId) });
      queryClient.invalidateQueries({ queryKey: qk.issueRuns(issueId) });
      navigate(wp(`/runs/${run.id}`));
    },
  });

  const mergeMutation = useMutation({
    mutationFn: (runId: string) => mergeRunPullRequest(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.issue(issueId) });
      queryClient.invalidateQueries({ queryKey: qk.issueRuns(issueId) });
      queryClient.invalidateQueries({ queryKey: qk.issueTimeline(issueId) });
      setConfirmMergePr(false);
      mergeMutation.reset();
    },
  });

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmMergePr, setConfirmMergePr] = useState(false);
  const deleteMutation = useMutation({
    mutationFn: () => deleteIssue(issueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.projectIssues(projectId) });
      navigate(wp(projectNav(projectQuery.data).selfTo));
    },
  });

  const approveMutation = useMutation({
    mutationFn: () => updateIssue(issueId, { approved: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.issue(issueId) });
      queryClient.invalidateQueries({ queryKey: qk.projectIssues(projectId) });
    },
  });

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const cancelMutation = useMutation({
    mutationFn: () => cancelRun(latest!.id),
    onSuccess: () => {
      setCancelDialogOpen(false);
      cancelMutation.reset();
      queryClient.invalidateQueries({ queryKey: qk.issue(issueId) });
      queryClient.invalidateQueries({ queryKey: qk.issueRuns(issueId) });
    },
  });

  const [confirmRunAgain, setConfirmRunAgain] = useState(false);
  // Owner-only one-off raised cost cap for a Run again after a cost-cap stop
  // (dollars, as typed). Seeded with the default each time the dialog opens.
  const [capOverrideInput, setCapOverrideInput] = useState('');
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [closeNote, setCloseNote] = useState('');
  const [closeResolution, setCloseResolution] = useState<IssueResolution>(IssueResolution.Fixed);
  const closeMutation = useMutation({
    mutationFn: () =>
      closeIssue(issueId, {
        resolution: closeResolution,
        note: closeNote.trim() || undefined,
      }),
    onSuccess: () => {
      setShowCloseDialog(false);
      setCloseNote('');
      queryClient.invalidateQueries({ queryKey: qk.issue(issueId) });
      queryClient.invalidateQueries({ queryKey: qk.projectIssues(projectId) });
      queryClient.invalidateQueries({ queryKey: qk.issueTimeline(issueId) });
    },
  });

  // Live comments + PM-thinking banner are on for every issue page now — the
  // Threads with the PM live at the project level under /projects/:id/threads.
  const { isPmThinking } = useIssueSocket(issueId);

  if (issueQuery.isLoading) return <LoadingState label="Loading issue…" />;
  if (issueQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(issueQuery.error, 'Failed to load issue')}
        onRetry={() => issueQuery.refetch()}
      />
    );
  }

  const issue = issueQuery.data!;
  const isActive =
    !!latest && !TERMINAL_RUN_STATUSES.includes(latest.status);

  // Budget stop: the latest build failed on a spend cap. `costCapUsd` (the
  // cap that run ran under) and the workspace cap come from the run detail.
  const budgetStop =
    latest && latest.status === RunStatus.Failed &&
    (latest.failureReason === 'cost_cap' || latest.failureReason === 'monthly_cap')
      ? latest.failureReason
      : null;
  const runDetail = latestRunDetailQuery.data;
  const runCapUsd = runDetail?.costCapUsd ?? null;
  const orgCapUsd = runDetail?.orgMaxRunCostUsd ?? null;
  const maxOverrideUsd =
    orgCapUsd !== null ? orgCapUsd * MAX_COST_CAP_OVERRIDE_MULTIPLIER : null;
  const canRaiseCap = budgetStop === 'cost_cap' && isWorkspaceOwner && runCapUsd !== null;
  // Default: double the cap that stopped the run, bounded by the server's max.
  const defaultOverrideUsd =
    runCapUsd !== null
      ? Math.min(runCapUsd * 2, maxOverrideUsd ?? Infinity)
      : null;
  const openRunAgain = () => {
    setCapOverrideInput(
      canRaiseCap && defaultOverrideUsd !== null ? defaultOverrideUsd.toFixed(2) : '',
    );
    setConfirmRunAgain(true);
  };
  const confirmRunAgainNow = () => {
    const parsed = Number.parseFloat(capOverrideInput);
    runMutation.mutate(
      canRaiseCap && capOverrideInput.trim() && Number.isFinite(parsed) ? parsed : undefined,
    );
  };

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={(() => {
          const nav = projectNav(projectQuery.data);
          return [
            { label: nav.rootLabel, to: wp(nav.rootTo) },
            { label: nav.name, to: wp(nav.selfTo) },
            { label: issue.code ? `${issue.code} ${issue.title}` : issue.title },
          ];
        })()}
      />

      <PageHeader
        title={issue.title}
        subtitle={[
          issue.code,
          issue.createdByEmail
            ? `created by ${personLabel(issue.createdByEmail, viewerEmail)} ${formatRelative(issue.createdAt)}`
            : null,
          `updated ${formatRelative(issue.updatedAt)}`,
        ]
          .filter(Boolean)
          .join(' · ')}
        stackActions
        actions={
          <>
            <IssueNavButton direction="previous" target={prevIssue} href={wp} />
            <IssueNavButton direction="next" target={nextIssue} href={wp} />
            {canMergePr &&
              latest &&
              latest.pullRequestUrl &&
              latest.pullRequestState === 'open' &&
              latest.autoMergeStatus === 'awaiting-review' && (
                <button
                  type="button"
                  className="btn-success"
                  disabled={mergeMutation.isPending}
                  onClick={() => setConfirmMergePr(true)}
                >
                  {mergeMutation.isPending ? (
                    <Spinner className="h-3.5 w-3.5" />
                  ) : (
                    <GitMerge className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  Merge PR
                </button>
              )}
            {issue.state !== 'closed' && canCloseIssue && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowCloseDialog(true)}
              >
                <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                Mark done
              </button>
            )}
            {issue.state !== 'closed' && canEditIssue && (
              <Link to={wp(`/issues/${issue.code ?? issue.id}/edit`)} className="btn-secondary">
                Edit
              </Link>
            )}
            {isActive && latest && (
              <>
                {canStopRun && (
                  <button
                    type="button"
                    className="btn-danger"
                    disabled={cancelMutation.isPending}
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    {cancelMutation.isPending && <Spinner className="h-3.5 w-3.5" />}
                    Cancel run
                  </button>
                )}
                <Link to={wp(`/runs/${latest.id}`)} className="btn-primary">
                  View live run
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </>
            )}
            {/* Approve / Send back / Run / Run again now live in the composer at
                the foot of the timeline (the single action surface). */}
            {canDeleteIssue && (
              <OverflowMenu
                items={[
                  {
                    label: 'Delete issue',
                    danger: true,
                    icon: <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />,
                    onClick: () => setConfirmDelete(true),
                  },
                ]}
              />
            )}
          </>
        }
      />

      {issue.threadOrigin && (
        <p className="text-xs text-ink-muted">
          Discovered in thread{' '}
          <Link
            to={wp(`${projectNav(projectQuery.data).selfTo}/threads/${issue.threadOrigin.threadId}`)}
            className="font-medium text-ink underline-offset-2 hover:underline"
          >
            “{issue.threadOrigin.threadTitle?.trim() || 'Untitled thread'}”
          </Link>
        </p>
      )}

      {approveMutation.isError && (
        <Callout tone="danger">
          {errorMessage(approveMutation.error, 'Could not approve issue')}
        </Callout>
      )}
      {runMutation.isError && (
        <Callout tone="danger">
          {errorMessage(runMutation.error, 'Could not start run')}
        </Callout>
      )}
      {closeMutation.isError && (
        <Callout tone="danger">
          {errorMessage(closeMutation.error, 'Could not close issue')}
        </Callout>
      )}
      {budgetStop === 'cost_cap' && (
        <Callout tone="warning" kicker="Stopped by a budget">
          Stopped by the per-run cost cap
          {runCapUsd !== null ? ` ($${runCapUsd.toFixed(2)})` : ''}. Nothing was pushed, so
          Run again starts from scratch and needs more headroom to finish.{' '}
          {isWorkspaceOwner
            ? 'Use Run again to raise the cap for that one run.'
            : 'Ask a workspace owner to Run again with a raised cap.'}
        </Callout>
      )}
      {budgetStop === 'monthly_cap' && (
        <Callout tone="warning" kicker="Stopped by a budget">
          The workspace monthly spend cap was reached. This run restarts automatically once
          the cap resets or is raised; Run again will fail until then.
        </Callout>
      )}

      <Modal
        open={showCloseDialog}
        onClose={closeMutation.isPending ? () => undefined : () => { setShowCloseDialog(false); setCloseNote(''); closeMutation.reset(); }}
        title="Mark as done"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              disabled={closeMutation.isPending}
              onClick={() => { setShowCloseDialog(false); setCloseNote(''); closeMutation.reset(); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={closeMutation.isPending}
              onClick={() => closeMutation.mutate()}
            >
              {closeMutation.isPending && <Spinner className="h-3.5 w-3.5" />}
              Mark done
            </button>
          </>
        }
      >
        {isActive && (
          <Callout tone="warning" className="mb-3">
            An agent run is currently in progress. Closing this issue will not stop the running agent — it will continue to completion and may push a branch or open a pull request.
          </Callout>
        )}
        {latest?.pullRequestUrl && latest.pullRequestState === PullRequestState.Open && (
          <Callout tone="warning" className="mb-3">
            This issue has an{' '}
            <a
              href={latest.pullRequestUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              open pull request
            </a>{' '}
            on GitHub. Closing the issue will not close the PR — you'll need to merge or close it manually.
          </Callout>
        )}
        <p className="mb-3 text-sm text-ink-muted">
          Close{' '}
          <span className="font-medium text-ink">{issue?.title}</span>{' '}
          without deleting it. The issue and its run history are preserved.
        </p>
        <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="close-resolution">
          Resolution
        </label>
        <select
          id="close-resolution"
          className="input mb-3 w-full"
          value={closeResolution}
          onChange={(e) => setCloseResolution(e.target.value as IssueResolution)}
          disabled={closeMutation.isPending}
        >
          <option value={IssueResolution.Fixed}>Fixed</option>
          <option value={IssueResolution.WontDo}>Won&apos;t do</option>
          <option value={IssueResolution.Duplicate}>Duplicate</option>
        </select>
        <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="close-note">
          Note (optional)
        </label>
        <textarea
          id="close-note"
          className="w-full rounded-md border border-line bg-surface-2 px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60"
          rows={3}
          maxLength={1000}
          placeholder="Why are you closing this?"
          value={closeNote}
          onChange={(e) => setCloseNote(e.target.value)}
          disabled={closeMutation.isPending}
        />
        {closeMutation.isError && (
          <div className="mt-3">
            <Callout tone="danger">
              {errorMessage(closeMutation.error, 'Could not close issue')}
            </Callout>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={cancelDialogOpen}
        title="Cancel run?"
        confirmLabel="Cancel run"
        busy={cancelMutation.isPending}
        error={
          cancelMutation.isError
            ? errorMessage(cancelMutation.error, 'Could not cancel run')
            : null
        }
        onConfirm={() => cancelMutation.mutate()}
        onClose={() => { setCancelDialogOpen(false); cancelMutation.reset(); }}
        message={
          <>
            This will stop the running agent immediately. Any uncommitted work inside the container will be lost.{' '}
            Any branch already pushed to GitHub will remain there.
          </>
        }
      />

      <ConfirmDialog
        open={confirmMergePr}
        title="Merge pull request?"
        confirmLabel="Merge pull request"
        busy={mergeMutation.isPending}
        error={
          mergeMutation.isError
            ? errorMessage(mergeMutation.error, 'Could not merge the pull request')
            : null
        }
        onConfirm={() => mergeMutation.mutate(latest!.id)}
        onClose={() => { setConfirmMergePr(false); mergeMutation.reset(); }}
        message={
          <>
            Merge the pull request for{' '}
            <span className="font-medium text-ink">{issue.title}</span>? This cannot be undone.
          </>
        }
      />

      <ConfirmDialog
        open={confirmRunAgain}
        title="Run again?"
        confirmLabel="Run again"
        busy={runMutation.isPending}
        error={
          runMutation.isError
            ? errorMessage(runMutation.error, 'Could not start run')
            : null
        }
        onConfirm={confirmRunAgainNow}
        onClose={() => { setConfirmRunAgain(false); runMutation.reset(); }}
        message={
          <>
            Start a new agent run on{' '}
            <span className="font-medium text-ink">{issue.title}</span>?{' '}
            The issue will be re-queued.
            {canRaiseCap && (
              <label className="mt-3 block" htmlFor="cost-cap-override">
                <span className="mb-1 block text-xs font-medium text-ink-muted">
                  Raise cap for this run to ($)
                </span>
                <input
                  id="cost-cap-override"
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={maxOverrideUsd ?? undefined}
                  className="input w-full"
                  value={capOverrideInput}
                  onChange={(e) => setCapOverrideInput(e.target.value)}
                  disabled={runMutation.isPending}
                />
                <span className="mt-1 block text-xs text-ink-faint">
                  Applies to this run only
                  {maxOverrideUsd !== null ? ` (max $${maxOverrideUsd.toFixed(2)})` : ''}. Leave
                  empty to keep the current cap.
                </span>
              </label>
            )}
          </>
        }
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete issue?"
        confirmLabel="Delete issue"
        busy={deleteMutation.isPending}
        error={
          deleteMutation.isError
            ? errorMessage(deleteMutation.error, 'Could not delete issue')
            : null
        }
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setConfirmDelete(false)}
        message={
          <>
            Delete{' '}
            <span className="font-medium text-ink">{issue.title}</span>{' '}
            and its run history? This cannot be undone.
          </>
        }
      />

      {/* Full header chrome + timeline. */}
      <>
          {/* Details strip: State, Resolution, and Tags on one row. */}
          <dl className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
            <div className="flex items-center gap-2">
              <dt className="text-ink-muted">State</dt>
              <dd>
                <StatusBadge status={issue.state} size="sm" />
              </dd>
            </div>
            <div className="flex items-center gap-2">
              <dt className="text-ink-muted">Assignee</dt>
              <dd>
                {issue.state !== 'closed' ? (
                  <AssigneeChip issue={issue} showReason autonomy={autonomy} />
                ) : (
                  <span className="text-ink-faint">—</span>
                )}
              </dd>
            </div>
            {issue.resolution && (
              <div className="flex items-center gap-2">
                <dt className="text-ink-muted">Resolution</dt>
                <dd>
                  <StatusBadge
                    status={IssueState.Closed}
                    resolution={issue.resolution}
                    size="sm"
                  />
                </dd>
              </div>
            )}
            <div className="flex items-center gap-2">
              <dt className="text-ink-muted">Tags</dt>
              <dd>
                {issue.tags && issue.tags.length > 0 ? (
                  <TagChips tags={issue.tags} />
                ) : (
                  <span className="text-ink-faint">—</span>
                )}
              </dd>
            </div>
          </dl>

          <div className="space-y-4">
            <SectionCard title="Brief">
              {issue.body.trim() ? (
                <Markdown>{issue.body}</Markdown>
              ) : (
                <p className="text-sm text-ink-faint">No brief written yet.</p>
              )}
            </SectionCard>

            {latestRunDetailQuery.data && latestRunDetailQuery.data.changedFiles.length > 0 && (
              <ChangesPanel
                files={latestRunDetailQuery.data.changedFiles}
                diff={latestRunDetailQuery.data.diff}
              />
            )}

            <IssueTimeline
              issueId={issueId}
              readOnly={issue.state === 'closed' || !canCommentOnIssue || isActive}
              heldForYou={
                issue.state === IssueState.Proposed &&
                canApproveIssue &&
                assigneeFor(issue).actor === 'you' &&
                // A plan waits for your Approve only on a paused (manual)
                // project — on autopilot the PM releases its own plan. Hold
                // the bar until the plan exists (the 'planned' tag); a
                // paused/unknown project keeps the direct-approve flow.
                ((autonomy ?? 'manual') === 'manual' ||
                  (issue.tags?.includes('planned') ?? false))
              }
              onApprove={canApproveIssue ? () => approveMutation.mutate() : undefined}
              approving={approveMutation.isPending}
              runnable={
                (issue.state === IssueState.Open || issue.state === IssueState.Failed) &&
                !isActive &&
                canTriggerRun
              }
              runAgain={!!latest}
              onRun={
                canTriggerRun
                  ? () => (latest ? openRunAgain() : runMutation.mutate(undefined))
                  : undefined
              }
              running={runMutation.isPending}
            />

            {isPmThinking && (
              <div className="flex items-center gap-2 rounded-lg border border-line bg-surface-2 px-4 py-3 text-sm text-ink-muted">
                <PmThinkingDots />
                PM is thinking…
              </div>
            )}
          </div>
        </>
    </div>
  );
}

function IssueNavButton({
  direction,
  target,
  href,
}: {
  direction: 'previous' | 'next';
  target: { id: string; code: string | null } | undefined;
  href: (path: string) => string;
}) {
  const label = direction === 'previous' ? 'Previous issue' : 'Next issue';
  const Icon = direction === 'previous' ? ChevronLeft : ChevronRight;
  const icon = <Icon className="h-3.5 w-3.5" aria-hidden="true" />;
  if (!target) {
    return (
      <button type="button" className="btn-ghost" aria-label={label} title={label} disabled>
        {icon}
      </button>
    );
  }
  return (
    <Link
      to={href(`/issues/${target.code ?? target.id}`)}
      className="btn-ghost"
      aria-label={label}
      title={label}
    >
      {icon}
    </Link>
  );
}
